import * as Schema from "effect/Schema";

import { IsoDateTime, TrimmedNonEmptyString } from "./baseSchemas";

export const RuntimeDiagnosticsTool = Schema.Struct({
  name: TrimmedNonEmptyString,
  status: Schema.Literals(["ok", "error"]),
  output: Schema.optionalKey(Schema.String),
  error: Schema.optionalKey(Schema.String),
});
export type RuntimeDiagnosticsTool = typeof RuntimeDiagnosticsTool.Type;

export const RuntimeDiagnosticsProcess = Schema.Struct({
  pid: Schema.Number,
  ppid: Schema.Number,
  stat: Schema.String,
  command: Schema.String,
  /**
   * `ps`'s `%cpu`, which is CPU time over the process's whole lifetime on
   * Linux and a recent-usage estimate on macOS — not an instantaneous reading
   * either way.
   *
   * Absent rather than zero when unmeasured: a server that predates this field
   * still decodes, and a row synthesised without a `ps` sample reads as
   * "unknown" instead of claiming the process used no CPU.
   */
  cpuPercent: Schema.optionalKey(Schema.Number),
  /** Resident set size in kibibytes, from `ps`'s `rss`. Absent when unmeasured. */
  rssKb: Schema.optionalKey(Schema.Number),
});
export type RuntimeDiagnosticsProcess = typeof RuntimeDiagnosticsProcess.Type;

export const AdminRuntimeLocalService = Schema.Struct({
  name: TrimmedNonEmptyString,
  status: Schema.Literals(["configured", "missing"]),
  url: Schema.optionalKey(Schema.String),
  port: Schema.optionalKey(Schema.String),
  detail: Schema.optionalKey(Schema.String),
});
export type AdminRuntimeLocalService = typeof AdminRuntimeLocalService.Type;

export const AdminCurrentRuntimeProcess = Schema.Struct({
  pid: Schema.Number,
  ppid: Schema.optionalKey(Schema.Number),
  cwd: Schema.optionalKey(Schema.String),
  platform: Schema.String,
  arch: Schema.String,
  nodeVersion: Schema.String,
  uptimeSeconds: Schema.Number,
  forgeHome: Schema.optionalKey(Schema.String),
  forgeDevInstance: Schema.optionalKey(Schema.String),
  forgePort: Schema.optionalKey(Schema.String),
  forgeHost: Schema.optionalKey(Schema.String),
});
export type AdminCurrentRuntimeProcess = typeof AdminCurrentRuntimeProcess.Type;

export const RuntimeDiagnosticsResponse = Schema.Struct({
  machineId: TrimmedNonEmptyString,
  fetchedAt: IsoDateTime,
  runtime: Schema.Struct({
    commit: Schema.optionalKey(Schema.String),
    sandboxRuntimeVersion: Schema.optionalKey(Schema.String),
    nodeEnv: Schema.optionalKey(Schema.String),
    cmmdRuntimeMode: Schema.optionalKey(Schema.Boolean),
    cmmdRuntimeId: Schema.optionalKey(Schema.String),
    cmmdRuntimeOrgId: Schema.optionalKey(Schema.String),
    cmmdRuntimeProjectId: Schema.optionalKey(Schema.String),
    cmmdRuntimeMachineId: Schema.optionalKey(Schema.String),
  }),
  tools: Schema.Array(RuntimeDiagnosticsTool),
  processes: Schema.Array(RuntimeDiagnosticsProcess),
  /**
   * Why `processes` is empty, when it is empty because the list could not be
   * read rather than because nothing is running.
   *
   * Cloud VMs run an image without `procps`, so `ps` is simply absent there.
   * Without this the reader gets an empty table captioned "no process data",
   * which says "nothing is running on this machine" — the opposite of true.
   */
  processCollectionError: Schema.optionalKey(Schema.String),
  currentProcess: Schema.optionalKey(AdminCurrentRuntimeProcess),
  localServices: Schema.optionalKey(Schema.Array(AdminRuntimeLocalService)),
});
export type RuntimeDiagnosticsResponse = typeof RuntimeDiagnosticsResponse.Type;
